import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetch2, fetch3, fetch5 } from "../commonApis/commonApis";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

const url = process.env.REACT_APP_BASE_URL;

export const useFetchClientTickets = (params: any = {}) => {
  const profile = useSelector((state: any) => state.auth?.profile);
  
  return useQuery({
    queryKey: ["clientTickets", profile?.id, params],
    enabled: !!profile?.id,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.append("page", params.page);
      if (params.limit) searchParams.append("limit", params.limit);
      if (params.status) searchParams.append("filters[status]", params.status);
      if (params.priority) searchParams.append("filters[priority]", params.priority);
      if (params.type) searchParams.append("filters[type]", params.type);
      
      const res = await fetch3(`${url}/api/client-ticket-management?${searchParams.toString()}`, "get");
      return res?.data ?? null;
    },
  });
};

export const useFetchTicketDetails = (ticketId: string) => {
  const profile = useSelector((state: any) => state.auth?.profile);
  
  return useQuery({
    queryKey: ["clientTicketDetails", ticketId, profile?.id],
    enabled: !!ticketId && !!profile?.id,
    queryFn: async () => {
      const res = await fetch3(`${url}/api/client-ticket-management/${ticketId}`, "get");
      return res?.data ?? null;
    },
  });
};

export const useCreateTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch2(`${url}/api/client-ticket-management`, data);
      if (!res.success) throw new Error(res.message || "Failed to create ticket");
      return res.data;
    },
    onSuccess: () => {
      toast.success("Ticket created successfully");
      queryClient.invalidateQueries({ queryKey: ["clientTickets"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to create ticket");
    }
  });
};

export const useAddTicketComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ticketId, data }: { ticketId: string; data: any }) => {
      const res = await fetch5(`${url}/api/client-ticket-management/${ticketId}/comments`, "post", data);
      if (!res.success) throw new Error(res.message || "Failed to add comment");
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clientTicketDetails", variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ["clientTickets"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to add comment");
    }
  });
};

export const useUpdateTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ticketId, data }: { ticketId: string; data: any }) => {
      const res = await fetch5(`${url}/api/client-ticket-management/${ticketId}`, "PATCH", data);
      if (!res.success) throw new Error(res.message || "Failed to update ticket");
      return res.data;
    },
    onSuccess: (_, variables) => {
      toast.success("Ticket updated successfully");
      queryClient.invalidateQueries({ queryKey: ["clientTicketDetails", variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ["clientTickets"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update ticket");
    }
  });
};

export const useCloseTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (ticketId: string) => {
      const res = await fetch5(`${url}/api/client-ticket-management/${ticketId}/close`, "PATCH", {});
      if (!res.success) throw new Error(res.message || "Failed to close ticket");
      return res.data;
    },
    onSuccess: (_, ticketId) => {
      toast.success("Ticket closed successfully");
      queryClient.invalidateQueries({ queryKey: ["clientTicketDetails", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["clientTickets"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to close ticket");
    }
  });
};

export const useReopenTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ticketId, reason }: { ticketId: string, reason: string }) => {
      const res = await fetch5(`${url}/api/client-ticket-management/${ticketId}/reopen`, "PATCH", { reason });
      if (!res.success) throw new Error(res.message || "Failed to reopen ticket");
      return res.data;
    },
    onSuccess: (_, variables) => {
      toast.success("Ticket reopened successfully");
      queryClient.invalidateQueries({ queryKey: ["clientTicketDetails", variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ["clientTickets"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to reopen ticket");
    }
  });
};
